class SwiperSlider extends HTMLElement {
  constructor() {
    super();
    this.slideContainer = this.querySelector('[data-swiper]:not([data-swiper-thumbs])');
    this.slides = this.querySelectorAll('[data-swiper-slide]');
    this.sliderOptions = this.parseOptions(this.dataset.options);
    this.activeIndex = 0;
    this.initialized = false;
    this.updateFormOnSlideChange = this.hasAttribute('data-update-form-on-change') || false;
    this.relatedForm = document.querySelector(this.getAttribute('data-related-form')) || null;
    this.thumbnails = this.querySelector('[data-swiper-thumbs]');

    if (!this.hasAttribute('disable-auto-init')) {
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    if (this.slideContainer && this.slides.length > 0) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initializeSwiper());
      } else {
        this.initializeSwiper();
      }
      document.addEventListener('shopify:section:load', () => this.initializeSwiper());
      window.addEventListener(
        'resize',
        this.debouncedResize(() => this.updateSliderSize(), 300)
      );
    } else {
      console.warn('SwiperSlider - Slider or Slides Not Found');
    }
  }

  goToSlide(index) {
    if (!this.slider || index === undefined) return;
    this.slider.progressLoop ? this.slider.slideToLoop(index) : this.slider.slideTo(index);
  }

  getSlide(index) {
    if (!this.slider || index === undefined) return;
    return this.slides[index];
  }

  parseOptions(options) {
    try {
      if (options) {
        const sanitizedOptions = options.replace(/'/g, '"');
        return JSON.parse(sanitizedOptions || '{}');
      }
      return {};
    } catch (error) {
      console.error('Error parsing options: ', error);
      return {};
    }
  }

  initializeSwiper() {
    if (!window.Swiper || this.initialized) return;

    try {
      this.setThumbnails();
      this.slider = new Swiper(this.slideContainer, this.sliderOptions);
      this.initialized = true;

      this.slider.on('slideChange', () => {
        this.updateSlideAttributes();

        if (this.updateFormOnSlideChange) {
          this.updateRelatedForm(this.getSlide(this.slider.realIndex));
        }

        if (this.hasAttribute('data-update-bg-hue-node')) {
          this.updateSlideHue(this.getSlide(this.slider.realIndex));
        } else if (this.hasAttribute('data-update-bg-node')) {
          this.updateSlideBackground(this.getSlide(this.slider.realIndex));
        } else if (this.hasAttribute('data-update-property-node')) {
          const node = document.querySelector(this.getAttribute('data-update-property-node'));
          const properties = this.getSlideProperties(this.getSlide(this.slider.realIndex));

          if (node && properties) {
            Object.entries(properties).forEach(([name, value]) => {
              this.updateCustomProperty(node, name, value);
            });
          }
        }

        this.preloadNearbyImages();
      });

      this.slider.on('reachEnd', () => this.setAttribute('data-reached-end', ''));
      this.slider.on('reachBeginning', () => this.setAttribute('data-reached-start', ''));
      this.slider.on('fromEdge', () => {
        this.removeAttribute('data-reached-end');
        this.removeAttribute('data-reached-start');
      });

      this.updateSlideAttributes();
      this.observeSlideChanges();

      if (this.hasAttribute('data-preload-images')) {
        this.preloadNearbyImages();
      }
    } catch (error) {
      console.error('SwiperSlider - Failed to initialize', error);
    }
  }

  updateCustomProperty(targetContainer, propertyName, propertyValue) {
    if (!targetContainer || !propertyName || propertyValue == null) return false;
    targetContainer.style.setProperty(`--${propertyName}`, propertyValue);
    return true;
  }

  getSlideProperties(slide) {
    if (!slide) return null;

    const desktop = slide.dataset.bgDesktop;
    const mobile = slide.dataset.bgMobile;
    const accent = slide.dataset.hueAccent;

    if (desktop || mobile || accent) {
      return {
        'bg-desktop': desktop,
        'bg-mobile': mobile,
        'custom-hue-accent': accent
      };
    }

    const dataAttr = slide.getAttribute('data-update-properties');
    if (!dataAttr) return null;

    try {
      return JSON.parse(dataAttr);
    } catch (e) {
      console.warn('SwiperSlider - Invalid JSON in data-update-properties', e);
      return null;
    }
  }

  updateSlideHue(slide) {
    const hueNode = document.querySelector(this.getAttribute('data-update-bg-hue-node'));
    if (!hueNode || !slide) return;

    const accent = slide.dataset.hueAccent;
    if (accent) {
      hueNode.style.setProperty('--custom-hue-accent', accent);
    } else {
      hueNode.style.removeProperty('--custom-hue-accent');
    }
  }

  updateSlideBackground(slide) {
    const bgNode = document.querySelector(this.getAttribute('data-update-bg-node'));
    if (!bgNode) return;

    const properties = this.getSlideProperties(slide);
    const desktopUrl = properties?.['bg-desktop'] || bgNode.dataset.defaultBgDesktop || '';
    const mobileUrl = properties?.['bg-mobile'] || properties?.['bg-desktop'] || bgNode.dataset.defaultBgMobile || desktopUrl;

    this.updateBackgroundImages(bgNode, desktopUrl, mobileUrl);

    const hueNode = this.hasAttribute('data-update-bg-hue-node')
      ? document.querySelector(this.getAttribute('data-update-bg-hue-node'))
      : bgNode.querySelector('[data-ip-slider-bg-hue]');

    if (hueNode) {
      const accent = properties?.['custom-hue-accent'];
      if (accent) {
        hueNode.style.setProperty('--custom-hue-accent', accent);
      } else {
        hueNode.style.removeProperty('--custom-hue-accent');
      }
    }
  }

  updateBackgroundImages(bgNode, desktopUrl, mobileUrl) {
    if (!bgNode) return;

    const picture = bgNode.querySelector('.ip-product-slider__bg-picture, [data-bg-img-desktop]')?.closest('picture')
      || bgNode.querySelector('picture');
    const mobileSource = bgNode.querySelector('[data-bg-source-mobile]');
    const desktopImg = bgNode.querySelector('[data-bg-img-desktop]');

    if (!desktopUrl) {
      bgNode.hidden = true;
      return;
    }

    bgNode.hidden = false;

    if (desktopImg) {
      desktopImg.src = desktopUrl;
    } else if (picture) {
      picture.querySelector('img')?.setAttribute('src', desktopUrl);
    } else {
      const img = document.createElement('img');
      img.className = 'ip-product-slider__bg-img';
      img.src = desktopUrl;
      img.alt = '';
      img.loading = 'eager';
      img.decoding = 'async';
      img.setAttribute('data-bg-img-desktop', '');

      const newPicture = document.createElement('picture');
      newPicture.className = 'ip-product-slider__bg-picture';

      const source = document.createElement('source');
      source.media = '(max-width: 767px)';
      source.srcset = mobileUrl || desktopUrl;
      source.setAttribute('data-bg-source-mobile', '');

      newPicture.appendChild(source);
      newPicture.appendChild(img);
      bgNode.prepend(newPicture);
    }

    const sourceEl = mobileSource || bgNode.querySelector('[data-bg-source-mobile]');
    if (sourceEl) {
      sourceEl.srcset = mobileUrl || desktopUrl;
    }
  }

  updateSliderSize() {
    this.slider?.updateSize();
  }

  updateSlideAttributes() {
    if (!this.slider) return;

    if (this.slider.isBeginning) {
      this.setAttribute('data-reached-start', '');
    } else {
      this.removeAttribute('data-reached-start');
    }

    if (this.slider.isEnd) {
      this.setAttribute('data-reached-end', '');
    } else {
      this.removeAttribute('data-reached-end');
    }
  }

  initializeSliderEventListeners() {
    if (this.slider) {
      this.slider.on('slideChange', () => {
        this.updateSlideAttributes();
      });
    }
  }

  updateRelatedForm(slide) {
    if (!this.relatedForm || !slide) return;

    const productData = JSON.parse(slide.querySelector('[data-swiper-product-info]').textContent);
    const updateFields = {
      'input[name="product-id"]': productData.id,
      '[data-form-update="variant_id"]': productData.variantId,
      '[data-form-update="title"]': productData.title,
      '[data-form-update="shortDescription"]': productData.shortDescription,
      '[data-form-update="price"]': productData.price,
      '[data-form-update="url"]': productData.url
    };

    Object.entries(updateFields).forEach(([selector, value]) => {
      const el = this.relatedForm.querySelector(selector);
      if (!el) return;
      if (el.tagName === 'INPUT' || el.tagName === 'OPTION') el.value = value;
      else if (el.tagName === 'A') el.href = value;
      else el.textContent = value;
    });

    // NEW: toggle Coming Soon state
    const addBtn = this.relatedForm.querySelector('button[name="add"]');
    const labelEl = addBtn?.querySelector('span');

    if (addBtn) {
      const isComingSoon = !!productData.comingSoon;
      if (isComingSoon) {
        addBtn.setAttribute('disabled', 'disabled');
      } else {
        addBtn.removeAttribute('disabled');
      }
      addBtn.classList.toggle('is-coming-soon', isComingSoon);
      if (labelEl) {
        labelEl.textContent = isComingSoon ? 'Coming soon' : 'Add to cart';
      }
    }
  }

  setThumbnails() {
    if (this.thumbnails) {
      const thumbOptions = this.parseOptions(this.thumbnails.dataset.swiperOptions);
      const thumbnailsSlider = new Swiper(this.thumbnails, thumbOptions);
      this.sliderOptions.thumbs = { swiper: thumbnailsSlider };
    }
  }

  debouncedResize(callback, delay) {
    let timeout;
    return () => {
      clearTimeout(timeout);
      timeout = setTimeout(callback, delay);
    };
  }

  observeSlideChanges() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && ['open', 'active', 'selected'].includes(mutation.attributeName)) {
          if (mutation.oldValue === null) {
            const slide = mutation.target.closest('[data-swiper-slide]');
            if (slide) {
              const index = Array.from(this.slides).indexOf(slide);
              if (index !== -1) {
                this.goToSlide(index);
              }
            }
          }
        }
      });
    });

    this.slides.forEach((slide) => {
      observer.observe(slide, {
        attributes: true,
        subtree: true,
        attributeFilter: ['open', 'active', 'selected'],
        attributeOldValue: true,
      });
    });
  }

  preloadImage(url) {
    const img = new Image();
    img.src = url;
  }

  preloadNearbyImages() {
    if (!this.slider || !this.hasAttribute('data-preload-images')) return;

    const indexesToPreload = [
      this.slider.realIndex - 1,
      this.slider.realIndex,
      this.slider.realIndex + 1
    ];

    indexesToPreload.forEach((i) => {
      const slide = this.getSlide(i);
      if (!slide) return;

      const properties = this.getSlideProperties(slide);
      if (!properties) return;

      const bg = properties['bg-desktop'] || properties['bg-image'];
      if (bg) {
        const url = bg.startsWith('url(') ? bg.match(/url\(["']?(.*?)["']?\)/)?.[1] : bg;
        if (url) this.preloadImage(url);
      }
      const mobileBg = properties['bg-mobile'];
      if (mobileBg && mobileBg !== bg) {
        this.preloadImage(mobileBg);
      }
    });
  }
}

customElements.define('swiper-slider', SwiperSlider);
